"use client";

import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { format, parseISO, isSameDay, addDays } from "date-fns";
import { Calendar, Clock, ChevronLeft, ChevronRight, Truck } from "lucide-react";

interface Slot {
  id: string;
  startAt: string;
  endAt: string;
  capacity: number;
  bookedCount: number;
}

interface PickupSlotPickerProps {
  areaId: string;
  selectedSlotId: string | null;
  onSelect: (slotId: string, slot: Slot) => void;
  cutoffTime?: string;
}

export function PickupSlotPicker({
  areaId,
  selectedSlotId,
  onSelect,
  cutoffTime = "14:00",
}: PickupSlotPickerProps) {
  const [loading, setLoading] = useState(true);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [startDate, setStartDate] = useState<Date>(new Date());

  useEffect(() => {
    if (areaId) {
      fetchSlots();
    }
  }, [areaId, startDate]);

  async function fetchSlots() {
    setLoading(true);
    try {
      const endDate = addDays(startDate, 6);
      const params = new URLSearchParams({
        areaId,
        slotType: "PICKUP",
        from: startDate.toISOString(),
        to: endDate.toISOString(),
      });

      const response = await fetch(`/api/public/slots?${params}`);
      if (response.ok) {
        const data = await response.json();
        setSlots(data.slots || []);
      }
    } catch (error) {
      console.error("Failed to fetch slots:", error);
    } finally {
      setLoading(false);
    }
  }

  function navigateWeek(direction: "prev" | "next") {
    setStartDate((current) => addDays(current, direction === "next" ? 7 : -7));
  }

  const days: Date[] = [];
  for (let i = 0; i < 7; i++) {
    days.push(addDays(startDate, i));
  }

  function getSlotsForDay(date: Date): Slot[] {
    return slots
      .filter((slot) => isSameDay(parseISO(slot.startAt), date))
      .sort(
        (a, b) => parseISO(a.startAt).getTime() - parseISO(b.startAt).getTime()
      );
  }

  function isSlotAvailable(slot: Slot): boolean {
    return slot.bookedCount < slot.capacity;
  }

  function formatSlotTime(slot: Slot): string {
    const start = parseISO(slot.startAt);
    const end = parseISO(slot.endAt);
    return `${format(start, "h:mm a")} - ${format(end, "h:mm a")}`;
  }

  function isCutoffEligible(slot: Slot): boolean {
    const slotDate = parseISO(slot.startAt);
    const slotHour = format(slotDate, "HH:mm");
    const today = new Date();
    const isToday = isSameDay(slotDate, today);

    if (!isToday) return true;

    return slotHour < cutoffTime;
  }

  const canGoPrev = startDate > new Date();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium text-slate-900">Select Pickup Slot</h3>
          <p className="text-sm text-muted-foreground">
            Choose when you'd like us to pick up your garments
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigateWeek("prev")}
            disabled={!canGoPrev}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" onClick={() => navigateWeek("next")}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-7 gap-2">
          {[...Array(7)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          {days.map((day) => {
            const daySlots = getSlotsForDay(day);
            const isPast = day < new Date() && !isSameDay(day, new Date());

            return (
              <Card
                key={day.toISOString()}
                className={cn(
                  "overflow-hidden",
                  isPast && "opacity-50"
                )}
              >
                <div className="bg-muted/50 px-3 py-2 text-center border-b">
                  <p className="text-xs text-muted-foreground">
                    {format(day, "EEE")}
                  </p>
                  <p className="font-medium">{format(day, "MMM d")}</p>
                </div>
                <CardContent className="p-2 space-y-1.5 max-h-48 overflow-y-auto">
                  {daySlots.length > 0 ? (
                    daySlots.map((slot) => {
                      const available = isSlotAvailable(slot);
                      const isSelected = selectedSlotId === slot.id;
                      const isFastTrack = isCutoffEligible(slot);

                      return (
                        <button
                          key={slot.id}
                          onClick={() => available && onSelect(slot.id, slot)}
                          disabled={!available || isPast}
                          className={cn(
                            "w-full px-2 py-1.5 text-xs rounded-md transition-colors text-left",
                            isSelected
                              ? "bg-primary text-primary-foreground"
                              : available && !isPast
                              ? "bg-muted hover:bg-primary/10"
                              : "bg-muted/50 text-muted-foreground cursor-not-allowed"
                          )}
                        >
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            <span>{format(parseISO(slot.startAt), "h:mm a")}</span>
                          </div>
                          {isFastTrack && available && (
                            <div className="mt-0.5 flex items-center gap-1 text-emerald-600">
                              <Truck className="h-3 w-3" />
                              <span className="text-[10px]">24h delivery</span>
                            </div>
                          )}
                        </button>
                      );
                    })
                  ) : (
                    <p className="text-xs text-muted-foreground text-center py-2">
                      No slots
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {selectedSlotId && (
        <Card className="border-primary bg-primary/5">
          <CardContent className="py-3 px-4">
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-primary" />
              <div>
                <p className="text-sm font-medium">Selected Pickup Slot</p>
                <p className="text-sm text-muted-foreground">
                  {slots.find((s) => s.id === selectedSlotId) &&
                    formatSlotTime(slots.find((s) => s.id === selectedSlotId)!)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
